import React, { useState, useRef, useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { useGetWorkspaceQuery, useUpdateWorkspaceMutation } from '@/services/workspaces/queries';
import { useUploadFileMutation } from '@/services/files/queries';
import { usePostHog } from 'posthog-js/react';

// Define initial color options
const initialColorOptions = [
  { name: 'Blue', value: '#1e3a8a' },
  { name: 'Red', value: '#dc2626' },
  { name: 'Teal', value: '#14b8a6' },
  { name: 'Pink', value: '#db2777' },
  { name: 'Orange', value: '#ea580c' },
  { name: 'Gray', value: '#6b7280' }
];

// Define form schema with Zod
const formSchema = z.object({
  name: z.string().min(2, {
    message: 'Workspace name must be at least 2 characters.'
  }),
  logo: z.instanceof(File).optional(),
  color: z.string()
});

type FormValues = z.infer<typeof formSchema>;

export default function WorkspaceSettings() {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [colorOptions, setColorOptions] = useState(initialColorOptions);
  const [hasLogoChanged, setHasLogoChanged] = useState(false);
  const logoRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const posthog = usePostHog();
  const { activeWorkspace } = useAuthStore();

  const { data: workspace } = useGetWorkspaceQuery(
    activeWorkspace || '',
    { enabled: !!activeWorkspace }
  );

  const updateWorkspaceMutation = useUpdateWorkspaceMutation(
    activeWorkspace || '',
    () => {
      posthog?.capture('form_submitted', { form_name: 'workspace_settings', success: true });
      toast.success('Workspace updated successfully');
    },
    (error) => {
      posthog?.capture('form_submitted', { form_name: 'workspace_settings', success: false });
      toast.error('Failed to update workspace');
      console.error('Workspace update error:', error);
    }
  );

  const uploadFileMutation = useUploadFileMutation(
    () => {
      toast.success('Logo uploaded successfully');
    },
    (error) => {
      toast.error('Failed to upload logo');
      console.error('Logo upload error:', error);
    }
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      color: initialColorOptions[0].value
    }
  });

  useEffect(() => {
    if (workspace) {
      // Prefill workspace name
      form.setValue('name', workspace.name || '');

      // Prefill theme color
      form.setValue('color', workspace.color || initialColorOptions[0].value);

      // Prefill logo preview if available
      if (workspace.logo) {
        setLogoPreview(workspace.logo);
      }

      // Add the current color to options if it's not already there
      if (
        workspace.color &&
        !colorOptions.some((opt) => opt.value === workspace.color)
      ) {
        setColorOptions([
          ...colorOptions,
          {
            name: `Custom ${colorOptions.length - initialColorOptions.length + 1}`,
            value: workspace.color
          }
        ]);
      }
    }
  }, [workspace, form, colorOptions]);

  // Handle form submission
  async function onSubmit(values: FormValues) {
    try {
      let logoUrl = workspace?.logo;

      // Upload logo if it has changed
      if (hasLogoChanged && values.logo) {
        const uploadResponse = await uploadFileMutation.mutateAsync({
          file: values.logo,
          name: values.logo.name,
          owner: {
            id: activeWorkspace || '',
            resourceType: 'Workspace'
          }
        });
        logoUrl = uploadResponse.url;
      }

      // Update workspace
      await updateWorkspaceMutation.mutateAsync({
        name: values.name,
        color: values.color,
        logo: logoUrl
      });
    } catch (error) {
      console.error('Workspace update error:', error);
    }
  }

  // Handle logo upload with 1MB limit
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;

    // Check file size (1MB = 1,048,576 bytes)
    if (file.size > 1048576) {
      toast.error('File size must be less than 1MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
    form.setValue('logo', file);
    setHasLogoChanged(true);
  };

  // Handle color input change
  const handleColorInputChange = (color: string) => {
    form.setValue('color', color);
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className='w-full max-w-2xl space-y-8 px-6'
      >
        {/* Logo Upload Section */}
        <div className='space-y-4'>
          <h2 className='text-lg font-semibold'>Logo</h2>
          <p className='text-sm text-muted-foreground'>
            Logo should not be more than 1MB
          </p>
          <div className='flex gap-4'>
            <div className='space-y-2'>
              <div
                className='relative flex h-24 w-48 cursor-pointer items-center justify-center rounded-lg border border-dashed p-4 hover:border-primary'
                onClick={() => logoRef.current?.click()}
              >
                {logoPreview ? (
                  <img
                    src={logoPreview}
                    alt='Logo Preview'
                    className='absolute inset-0 h-full w-full object-contain p-2'
                  />
                ) : (
                  <span className='text-sm text-muted-foreground'>
                    {workspace?.logo ? 'Click or drag to change logo' : 'Click to upload'}
                  </span>
                )}
                <input
                  type='file'
                  ref={logoRef}
                  onChange={handleLogoUpload}
                  className='hidden'
                  accept='image/*'
                />
              </div>
            </div>
          </div>
        </div>

        {/* Theme Color Picker */}
        <FormField
          control={form.control}
          name='color'
          render={({ field }) => (
            <FormItem className='space-y-4'>
              <FormLabel className='text-lg font-semibold'>
                Theme color
              </FormLabel>
              <FormDescription>Choose a theme color</FormDescription>
              <FormControl>
                <div className='flex flex-wrap items-center gap-2'>
                  {colorOptions.map((color) => (
                    <button
                      key={color.value}
                      type='button'
                      className={cn(
                        'h-8 w-8 rounded-full transition-all',
                        field.value === color.value
                          ? 'ring-2 ring-black ring-offset-2'
                          : ''
                      )}
                      style={{ backgroundColor: color.value }}
                      onClick={() => field.onChange(color.value)}
                      aria-label={`Select ${color.name}`}
                      title={color.name}
                    />
                  ))}
                  <div className='relative'>
                    <button
                      type='button'
                      className='flex h-8 w-8 items-center justify-center rounded-full border-2 border-dashed border-gray-300 transition-colors hover:border-gray-400'
                      onClick={() => setShowColorPicker(!showColorPicker)}
                      aria-label='Custom color picker'
                    >
                      <PlusIcon className='h-4 w-4' />
                    </button>
                    {showColorPicker && (
                      <div className='absolute bottom-10 left-0 z-10 rounded-md border bg-white p-2 shadow-lg'>
                        <div className='space-y-2'>
                          <Input
                            type='color'
                            value={field.value}
                            onChange={(e) => handleColorInputChange(e.target.value)}
                            className='h-10 w-full'
                          />
                          <div className='flex items-center justify-between'>
                            <span className='text-sm'>{field.value}</span>
                            <Button
                              type='button'
                              size='sm'
                              onClick={() => setShowColorPicker(false)}
                            >
                              Done
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Workspace Name Field */}
        <FormField
          control={form.control}
          name='name'
          render={({ field }) => (
            <FormItem className='space-y-2'>
              <FormLabel>Workspace name</FormLabel>
              <FormControl>
                <Input placeholder='geo-rolodex' {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className='flex justify-between pt-6'>
          <Button type='button' variant='outline' onClick={() => navigate(-1)}>
            Back
          </Button>
          <Button
            type='submit'
            isLoading={updateWorkspaceMutation.isPending || uploadFileMutation.isPending}
            trackingEvent='workspace_settings_saved'
            trackingProperties={{ location: 'workspace_settings' }}
          >
            Apply changes
          </Button>
        </div>
      </form>
    </Form>
  );
}

function PlusIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      xmlns='http://www.w3.org/2000/svg'
      width='24'
      height='24'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M5 12h14' />
      <path d='M12 5v14' />
    </svg>
  );
}
